import React, { useState } from 'react';
import {
  GitCompare,
  MessageSquare,
  Mail,
  Check,
  Clock
} from 'lucide-react';
import { useGst } from '../context/GstContext';
import {
  runGstr2bReconciliation,
  ReconMatchResult
} from '../services/reconciliationService';
import { ExtractedGstInvoice, ImsAction } from '../types/gst';
import { ReconciliationKpiCards } from '../components/reconciliation/ReconciliationKpiCards';
import { VendorNudgeModal } from '../components/reconciliation/VendorNudgeModal';
import { toast } from '../components/ui';

export const ReconciliationPage: React.FC = () => {
  const { invoices, gstr2bRecords, updateInvoice, clientLegalName, activeGstin, taxPeriod, financialYear } = useGst();

  const [activeFilter, setActiveFilter] = useState<'ALL' | 'EXACT_MATCH' | 'PARTIAL_MATCH' | 'MISSING_IN_2B' | 'BLOCKED_17_5'>('ALL');
  const [selectedNudgeItem, setSelectedNudgeItem] = useState<ReconMatchResult | null>(null);
  const [imsDecisions, setImsDecisions] = useState<Record<string, ImsAction>>({});

  // Run deterministic 3-way reconciliation with saved IMS actions
  const { results, summary } = runGstr2bReconciliation(invoices, gstr2bRecords, imsDecisions);

  const filteredResults = results.filter((res) => {
    if (activeFilter === 'ALL') return true;
    if (activeFilter === 'BLOCKED_17_5') return res.invoice.itc_eligibility === 'BLOCKED_17_5';
    return res.reconStatus === activeFilter;
  });

  const handleSetImsAction = (invoiceId: string, action: ImsAction) => {
    setImsDecisions(prev => ({ ...prev, [invoiceId]: action }));
    toast.success(`IMS action for invoice set to ${action}`);
  };

  const handleBulkAcceptExact = () => {
    const updated = { ...imsDecisions };
    results.forEach(r => {
      if (r.reconStatus === 'EXACT_MATCH') {
        updated[r.invoice.id] = 'ACCEPT';
      }
    });
    setImsDecisions(updated);
    toast.success(`Accepted all ${summary.matchedCount} exact match records in IMS`);
  };

  const handleBulkPendingMissing = () => {
    const updated = { ...imsDecisions };
    results.forEach(r => {
      if (r.reconStatus === 'MISSING_IN_2B') {
        updated[r.invoice.id] = 'PENDING';
      }
    });
    setImsDecisions(updated);
    toast.success(`Marked all ${summary.missingIn2bCount} missing records as PENDING in IMS`);
  };

  const handleAcceptPortalValue = async (res: ReconMatchResult) => {
    if (!res.matched2bRecord) return;
    const portalTax = (res.matched2bRecord.integrated_tax || 0) +
      (res.matched2bRecord.central_tax || 0) +
      (res.matched2bRecord.state_tax || 0) +
      (res.matched2bRecord.cess_amount || 0);

    const updated: ExtractedGstInvoice = {
      ...res.invoice,
      total_tax: portalTax,
      recon_status: 'EXACT_MATCH'
    };

    await updateInvoice(updated, `ACCEPTED_GSTR2B_PORTAL_TAX_₹${portalTax}`);
    toast.success(`Adjusted invoice #${res.invoice.invoice_number} to portal tax value of ₹${portalTax}`);
  };

  const handleKeepBooksValue = async (res: ReconMatchResult) => {
    const updated: ExtractedGstInvoice = {
      ...res.invoice,
      recon_status: 'EXACT_MATCH'
    };
    await updateInvoice(updated, 'CONFIRMED_BOOKS_VALUE_BY_USER');
    toast.success(`Retained books value for #${res.invoice.invoice_number}`);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Title & Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <GitCompare className="h-6 w-6 text-cyan-400" />
            <span>3-Way GSTR-2B Reconciliation & IMS Hub</span>
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Statutory multi-tier cross-comparison (Books &bull; 2B Portal &bull; Sec 17(5)) with GSTN IMS Action Decisions
          </p>
        </div>

        <div className="flex items-center gap-2">
          {summary.matchedCount > 0 && (
            <button
              onClick={handleBulkAcceptExact}
              className="flex items-center gap-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 text-xs font-semibold border border-emerald-500/30 transition shadow-sm"
            >
              <Check className="h-3.5 w-3.5" />
              <span>Accept All Exact ({summary.matchedCount})</span>
            </button>
          )}

          {summary.missingIn2bCount > 0 && (
            <button
              onClick={handleBulkPendingMissing}
              className="flex items-center gap-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 px-3 py-1.5 text-xs font-semibold border border-amber-500/30 transition shadow-sm"
            >
              <Clock className="h-3.5 w-3.5" />
              <span>Defer Missing to Next Month ({summary.missingIn2bCount})</span>
            </button>
          )}

          <div className="rounded-xl border border-white/10 bg-[#131316] px-4 py-1.5 text-xs text-slate-300">
            Period: <span className="font-bold text-white font-mono">{taxPeriod} {financialYear}</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <ReconciliationKpiCards summary={summary} />

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-3 text-xs">
        {[
          { key: 'ALL', label: `All Bills (${results.length})` },
          { key: 'EXACT_MATCH', label: `Exact Matches (${summary.matchedCount})` },
          { key: 'PARTIAL_MATCH', label: `Partial Mismatches (${summary.partialCount})` },
          { key: 'MISSING_IN_2B', label: `Missing in 2B (${summary.missingIn2bCount})` },
          { key: 'BLOCKED_17_5', label: `Blocked Sec 17(5) (${summary.blockedCount})` }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key as any)}
            className={`px-3.5 py-1.5 rounded-xl font-medium transition ${
              activeFilter === tab.key
                ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reconciliation Table with IMS Actions */}
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#131316]">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0e1015] text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-white/10">
            <tr>
              <th className="py-3 px-4">Invoice #</th>
              <th className="py-3 px-4">Vendor & GSTIN</th>
              <th className="py-3 px-4 text-right">Books Tax (₹)</th>
              <th className="py-3 px-4 text-right">Portal 2B Tax (₹)</th>
              <th className="py-3 px-4 text-right">Variance</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-center">GSTN IMS Action</th>
              <th className="py-3 px-4">Audit Diagnosis & Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 font-mono text-slate-300">
            {filteredResults.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-slate-500 font-sans">
                  No records matching active reconciliation filter.
                </td>
              </tr>
            ) : (
              filteredResults.map((res, idx) => {
                const recTax = res.matched2bRecord
                  ? (res.matched2bRecord.integrated_tax || 0) +
                    (res.matched2bRecord.central_tax || 0) +
                    (res.matched2bRecord.state_tax || 0) +
                    (res.matched2bRecord.cess_amount || 0)
                  : 0;

                const activeIms = imsDecisions[res.invoice.id] || res.recommendedImsAction || 'PENDING';

                return (
                  <tr key={idx} className="hover:bg-white/[0.03] transition">
                    <td className="py-3 px-4 font-bold text-white">
                      {res.invoice.invoice_number}
                      <div className="text-[10px] text-slate-500 font-normal">{res.invoice.invoice_date}</div>
                    </td>
                    <td className="py-3 px-4 font-sans">
                      <div className="font-semibold text-slate-200">{res.invoice.supplier_name}</div>
                      <div className="font-mono text-[10px] text-slate-400">{res.invoice.supplier_gstin || 'No GSTIN'}</div>
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-white">
                      {(res.invoice.total_tax || 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-cyan-300">
                      {res.matched2bRecord ? recTax.toFixed(2) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {res.taxVariance > 0 ? (
                        <span className="text-amber-400 font-bold">₹{res.taxVariance.toFixed(2)}</span>
                      ) : (
                        <span className="text-emerald-400 font-bold">₹0.00</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center font-sans">
                      <span
                        className={`inline-block rounded px-2 py-0.5 text-[10px] font-bold ${
                          res.reconStatus === 'EXACT_MATCH'
                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            : res.reconStatus === 'PARTIAL_MATCH'
                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                        }`}
                      >
                        {res.reconStatus}
                      </span>
                    </td>

                    {/* GSTN IMS Action Decision */}
                    <td className="py-3 px-4 text-center font-sans">
                      <div className="inline-flex rounded-lg border border-white/10 bg-[#0e1015] p-0.5 text-[10px]">
                        <button
                          onClick={() => handleSetImsAction(res.invoice.id, 'ACCEPT')}
                          className={`px-2 py-0.5 rounded font-bold transition ${
                            activeIms === 'ACCEPT'
                              ? 'bg-emerald-500 text-black shadow-sm'
                              : 'text-slate-400 hover:text-white'
                          }`}
                          title="Accept in IMS (Claim ITC in 3B)"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => handleSetImsAction(res.invoice.id, 'PENDING')}
                          className={`px-2 py-0.5 rounded font-bold transition ${
                            activeIms === 'PENDING'
                              ? 'bg-amber-500 text-black shadow-sm'
                              : 'text-slate-400 hover:text-white'
                          }`}
                          title="Keep Pending in IMS (Defer claim)"
                        >
                          Pending
                        </button>
                        <button
                          onClick={() => handleSetImsAction(res.invoice.id, 'REJECT')}
                          className={`px-2 py-0.5 rounded font-bold transition ${
                            activeIms === 'REJECT'
                              ? 'bg-rose-500 text-white shadow-sm'
                              : 'text-slate-400 hover:text-white'
                          }`}
                          title="Reject in IMS (Ineligible bill)"
                        >
                          Reject
                        </button>
                      </div>
                    </td>

                    <td className="py-3 px-4 font-sans text-xs">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] text-slate-400 leading-snug">{res.explanation}</span>

                        {res.reconStatus === 'MISSING_IN_2B' && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => setSelectedNudgeItem(res)}
                              className="flex items-center gap-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-2 py-1 text-[11px] font-semibold border border-emerald-500/30 transition"
                            >
                              <MessageSquare className="h-3 w-3 text-emerald-400" />
                              <span>WhatsApp Nudge</span>
                            </button>
                            <button
                              onClick={() => setSelectedNudgeItem(res)}
                              className="flex items-center gap-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-2 py-1 text-[11px] font-semibold border border-cyan-500/30 transition"
                            >
                              <Mail className="h-3 w-3 text-cyan-400" />
                              <span>Email</span>
                            </button>
                          </div>
                        )}

                        {res.reconStatus === 'PARTIAL_MATCH' && res.matched2bRecord && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => handleAcceptPortalValue(res)}
                              className="rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-2 py-1 text-[10px] font-semibold border border-cyan-500/30 transition"
                            >
                              Accept Portal (₹{recTax.toFixed(2)})
                            </button>
                            <button
                              onClick={() => handleKeepBooksValue(res)}
                              className="rounded-lg bg-white/10 hover:bg-white/20 text-slate-300 px-2 py-1 text-[10px] font-semibold transition"
                            >
                              Keep Books (₹{(res.invoice.total_tax || 0).toFixed(2)})
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 1-Click WhatsApp & Email Follow-up Modal */}
      <VendorNudgeModal
        selectedNudgeItem={selectedNudgeItem}
        onClose={() => setSelectedNudgeItem(null)}
        clientLegalName={clientLegalName}
        activeGstin={activeGstin}
      />
    </div>
  );
};
